import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { FC, useCallback, useState } from 'react';
import {
  EXCLUDED_FILTERS_CATEGORY_LIST,
  FiltersState,
  NonEmptyArray,
  SelectedFilterCategory,
  SelectedFilterChipParams,
} from '../../../../../shared/types/common';
import { StyledFontAwesomeIconMui5 } from '../../../../../components/atoms/FontAwesomeIcon';
import { faFilterCircleDollar } from '@fortawesome/free-solid-svg-icons/faFilterCircleDollar';
import Popover from '../../../../../components/molecules/Popover';
import { styled } from '@mui/material/styles';
import { SelectedFilterChip } from './SelectedFilterChip';
import isNumber from 'lodash/isNumber';
import { getMonthlyPaymentIndex } from '../../../../../shared/utils/functions';
import { AVAILABLE_MONTHLY_PAYMENTS } from '../../../../../shared/utils/constants';
import { useFilters } from '../../../../../services/filters/hooks/useFilters';
import { FilterDividerMui5 } from '../components';

const Stack = styled(Box)(
  ({ theme }) => `
  &.MuiBox-root {
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(0.5)};
  }`,
);

// noinspection CssUnusedSymbol
const Label = styled(Typography)`
  &.MuiTypography-root {
    text-transform: uppercase;
  }
`;

const Icon = styled(StyledFontAwesomeIconMui5)(
  ({ theme }) => `
  margin-right: ${theme.spacing(1)};
  color: ${theme.palette.primary.main};`,
);
const getOverwriteLabel = (
  category: SelectedFilterCategory,
): Pick<SelectedFilterChipParams, 'overwriteLabel'> => {
  switch (category) {
    case 'hwyEco':
      return { overwriteLabel: ['Highway (MPG)', undefined] };

    case 'cityEco':
      return { overwriteLabel: ['City (MPG)', undefined] };

    case 'premium':
      return { overwriteLabel: ['OEM options', undefined] };

    default:
      return {};
  }
};

const getGenericChipParamsList = (
  filters: FiltersState,
): SelectedFilterChipParams[] =>
  Object.keys(filters).reduce((acc, category: SelectedFilterCategory) => {
    if (EXCLUDED_FILTERS_CATEGORY_LIST.includes(category)) {
      return acc;
    }

    acc.push({
      category: category,
      value: filters[category],
      ...getOverwriteLabel(category),
    });

    return acc;
  }, [] as SelectedFilterChipParams[]);

const getPremiumOptionsChipParams = (
  filters: FiltersState,
): SelectedFilterChipParams[] =>
  filters.premium?.map((option) => ({
    category: 'premium',
    value: option,
    ...getOverwriteLabel('premium'),
  })) || [];

const getMonthlyBudgetChipParamsList = (
  filters: FiltersState,
): SelectedFilterChipParams[] => {
  const { minMonthlyPayment, maxMonthlyPayment } = filters;
  const index = getMonthlyPaymentIndex({
    minMonthlyPayment,
    maxMonthlyPayment,
  });

  if (![minMonthlyPayment, maxMonthlyPayment].every(isNumber) || index < 0) {
    return [];
  }

  return [
    {
      category: 'monthlyBudget',
      value: [minMonthlyPayment, maxMonthlyPayment],
      overwriteLabel: [undefined, AVAILABLE_MONTHLY_PAYMENTS[index].label],
    },
  ];
};

const getFeaturesChipParams = (
  filters: FiltersState,
): SelectedFilterChipParams[] =>
  filters.feature?.map((option) => ({
    category: 'feature',
    value: option,
    ...getOverwriteLabel('feature'),
  })) || [];

const getFeaturedAuctionsParams = (
  filters: FiltersState,
): SelectedFilterChipParams[] => {
  if (!filters.is_featured) {
    return [];
  }
  return [
    {
      category: 'is_featured',
      value: 'Limited Offers',
      overwriteLabel: ['Offer Type', undefined],
    },
  ];
};

const getChipParamsList = (filters: FiltersState) => {
  const params = getGenericChipParamsList(filters);
  params.push(...getPremiumOptionsChipParams(filters));
  params.push(...getMonthlyBudgetChipParamsList(filters));
  params.push(...getFeaturesChipParams(filters));
  params.push(...getFeaturedAuctionsParams(filters));

  return params;
};

const onDeleteSelectedFilter =
  (
    filters: FiltersState,
    resetFiltersBy: (filtersToReset: (keyof FiltersState)[]) => void,
    setFiltersValue: (filters: FiltersState) => void,
  ) =>
  ({ category, value }) => {
    switch (category) {
      case 'monthlyBudget': {
        resetFiltersBy(['minMonthlyPayment', 'maxMonthlyPayment']);
        break;
      }

      case 'premium': {
        const premiumFilters = filters.premium?.filter(
          (option) => option !== (value as string),
        );
        premiumFilters.length === 0
          ? resetFiltersBy(['premium'])
          : setFiltersValue({
              premium: premiumFilters as NonEmptyArray<string>,
            });
        break;
      }

      case 'feature': {
        const featureFilters = filters.feature?.filter(
          (option) => option !== (value as string),
        );
        featureFilters.length === 0
          ? resetFiltersBy(['feature'])
          : setFiltersValue({
              feature: featureFilters as NonEmptyArray<string>,
            });
        break;
      }

      default: {
        resetFiltersBy([category]);
      }
    }
  };

type Props = {
  maxVisibleTags?: number;
};

export const SelectedFiltersWithDivider: FC<Props> = ({
  maxVisibleTags = 5,
}) => {
  const { filters, setFiltersValue, resetFiltersBy } = useFilters();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const toggleOpenCallback = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, [setIsOpen]);

  const selectedFiltersList = getChipParamsList(filters);
  const isHideButtonVisible = selectedFiltersList.length > maxVisibleTags;
  const moreCount = selectedFiltersList.length - maxVisibleTags;

  const isClearButtonVisible = selectedFiltersList.length > 0;
  const clearSelectedCallback = useCallback(() => {
    const categoriesToClear: SelectedFilterCategory[] = [
      'minMonthlyPayment',
      'maxMonthlyPayment',
      'premium',
    ];
    selectedFiltersList.forEach((param) => {
      const { category } = param;
      if (category === 'monthlyBudget') {
        return;
      }
      categoriesToClear.push(category);
    });
    resetFiltersBy(categoriesToClear);
  }, [resetFiltersBy, selectedFiltersList]);

  if (selectedFiltersList.length === 0) {
    return null;
  }

  return (
    <>
      <Label gutterBottom>
        <Icon icon={faFilterCircleDollar} />
        Selected Filters{' '}
        <Popover>
          The filters you selected are displayed here. You can remove
          unnecessary ones by clicking on the cross.
        </Popover>
      </Label>
      <Stack>
        {(isOpen
          ? selectedFiltersList
          : selectedFiltersList.slice(0, maxVisibleTags)
        ).map((chipParams) => (
          <SelectedFilterChip
            key={String(chipParams.category) + String(chipParams.value)}
            {...chipParams}
            onDelete={onDeleteSelectedFilter(
              filters,
              resetFiltersBy,
              setFiltersValue,
            )}
          />
        ))}
        {isClearButtonVisible && (
          <ClearChipButton
            clearSelected={clearSelectedCallback}
            selectedCount={selectedFiltersList.length}
          />
        )}
        {isHideButtonVisible && (
          <HideChipButton
            isOpen={isOpen}
            moreCount={moreCount}
            toggleOpen={toggleOpenCallback}
          />
        )}
      </Stack>
      <FilterDividerMui5 />
    </>
  );
};

const HideChipButton: FC<{
  isOpen: boolean;
  moreCount: number;
  toggleOpen: () => void;
}> = ({ isOpen, toggleOpen, moreCount }) => {
  return (
    <Chip
      label={
        <Label variant='caption' color='primary'>
          {isOpen ? 'hide' : `+${moreCount} more`}
        </Label>
      }
      variant='outlined'
      onClick={toggleOpen}
    />
  );
};

const ClearChipButton: FC<{
  clearSelected: () => void;
  selectedCount: number;
}> = ({ clearSelected, selectedCount }) => {
  const labelText = `clear all (${selectedCount})`;
  return (
    <Chip
      label={
        <Label variant='caption' color='primary'>
          {labelText}
        </Label>
      }
      variant='outlined'
      onClick={clearSelected}
    />
  );
};
